import { useTranslation } from "react-i18next";
import type { MetricKey } from "@onetasklist/shared/viewmodels/board/board-overview/boardOverviewMetrics.types";

const LABEL_KEYS: Record<MetricKey, string> = {
  activeTasks: "board.overview.activeTasksLabel",
  openSubTasks: "board.overview.openSubTasksLabel",
  overdueTasks: "board.overview.overdueTasksLabel",
  atRiskTasks: "board.overview.atRiskTasksLabel",
  oldTasks: "board.overview.oldTasksLabel",
  avgAge: "board.overview.avgAgeLabel",
  doneTasks: "board.overview.doneTasksLabel",
};

const ARIA_KEYS: Record<MetricKey, string> = {
  activeTasks: "board.overview.activeTasksAriaLabel",
  openSubTasks: "board.overview.openSubTasksAriaLabel",
  overdueTasks: "board.overview.overdueTasksAriaLabel",
  atRiskTasks: "board.overview.atRiskTasksAriaLabel",
  oldTasks: "board.overview.oldTasksAriaLabel",
  avgAge: "board.overview.avgAgeAriaLabel",
  doneTasks: "board.overview.doneTasksAriaLabel",
};

const DESCRIPTION_KEYS: Record<MetricKey, string> = {
  activeTasks: "board.overview.activeTasksDescription",
  openSubTasks: "board.overview.openSubTasksDescription",
  overdueTasks: "board.overview.overdueTasksDescription",
  atRiskTasks: "board.overview.atRiskTasksDescription",
  oldTasks: "board.overview.oldTasksDescription",
  avgAge: "board.overview.avgAgeDescription",
  doneTasks: "board.overview.doneTasksDescription",
};

export function useBoardOverviewText(
  t: ReturnType<typeof useTranslation>["t"],
) {
  return {
    title: t("board.overview.title"),
    dismiss: t("board.overview.dismiss"),
    getMetricLabel: (metricKey: MetricKey) => t(LABEL_KEYS[metricKey]),
    /**
     * Accessible name for a metric tile, including its value.
     *
     * The value must be interpolated here rather than left to the tile's text
     * content: an aria-label on the tile overrides that content, so a label
     * without the value leaves screen reader users unable to read the metric.
     */
    getMetricAriaLabel: (metricKey: MetricKey, value: number | string) =>
      t(ARIA_KEYS[metricKey], { value }),
    /** What the metric counts, and what pressing it filters to. */
    getMetricDescription: (metricKey: MetricKey) =>
      t(DESCRIPTION_KEYS[metricKey]),
    getOverflowAriaLabel: (overflowCount: number) =>
      t("board.overview.overflowAriaLabel", { count: overflowCount }),
  };
}
